import type { EventOutcome } from "@/context/pending-workflows/PendingWorkflowsContext";

/**
 * Lo que dicen "Activar" y "Publicar" en Ventas B2B cuando algo no salió entero.
 *
 * Desde trd-core v1.72.0 (#1153) las dos respuestas traen qué se salteó y por
 * qué. Antes contestaban sólo "listo", y el proveedor se enteraba del precio en
 * 0 cuando un cliente se lo reclamaba: el caso Old Prince de BigSup.
 *
 * Todo tolera la respuesta vieja. Prod corre core v1.69.2, que no trae
 * `skipped` ni `images`, y sin esos campos el aviso es el de siempre.
 */

/** Un toast listo para `toast()`. */
export type Aviso = {
  title: string;
  description: string;
  variant: "success" | "warning" | "info";
  duration: number;
};

/** Por qué core no pudo rehacer el precio de una variante, y qué hacer. */
export type MotivoSinRecalcular = {
  /** Sin número, para que sirva con 1 y con 20: "2 sin costo…". */
  que: string;
  /** Qué lo arregla, o por qué nada lo arregla, para `productos` productos. */
  queHacer: (productos: number) => string;
  /** La pantalla donde se arregla, si es otra. */
  donde?: { pantalla: string; ruta: string };
};

const PRECIOS_Y_COSTOS = { pantalla: "Precios y costos", ruta: "/precios" };

/**
 * `ResnapshotSkipReason` en `services/resnapshot-product-pricing.ts` (core
 * v1.72.0), en el orden en que core los descarta: una variante sin precio
 * nunca llega a "margen desconocido".
 *
 * Una tabla para todos los que lo dicen: los toasts de "Activar" y
 * "Actualizar", el aviso de Productos publicados y la ficha de cada producto.
 *
 * - `sin-precio`: ninguna fila de precio ACTIVA del negocio con costo y
 *   mayorista mayores que 0 (`variant-price-selection.ts`, #1145). Alcanza con
 *   que falte uno de los dos.
 * - `precio-invalido`: el mayorista, con el margen de la publicación, queda
 *   por debajo del costo (`validateVariantPricing`).
 * - `margen-desconocido`: publicada antes de v1.51.2, sin la publicación
 *   original donde buscar el margen. Ni "Actualizar" ni "Activar" lo fijan:
 *   lo único que cambia el precio es cargarlo a mano.
 */
export const MOTIVOS_SIN_RECALCULAR: Record<string, MotivoSinRecalcular> = {
  "sin-variante-local": {
    que: "sin una variante vinculada en tu inventario",
    queHacer: (n) =>
      `No hay de dónde tomar el precio: si ya no ${
        n === 1 ? "lo vendés, desactivalo" : "los vendés, desactivalos"
      }.`,
  },
  "sin-precio": {
    que: "sin costo o sin precio mayorista en tu inventario",
    queHacer: () => "Cargá el costo y el precio mayorista en Precios y costos.",
    donde: PRECIOS_Y_COSTOS,
  },
  "margen-desconocido": {
    que: "sin el margen de publicación guardado",
    queHacer: (n) =>
      n === 1
        ? 'Se publicó antes de que se guardara el margen, y no lo adivinamos para no bajarte el precio. Si querés cambiarle el precio, usá "Editar precios".'
        : 'Se publicaron antes de que se guardara el margen, y no lo adivinamos para no bajarte el precio. Si querés cambiarles el precio, usá "Editar precios" en cada uno.',
  },
  "precio-invalido": {
    que: "con el precio mayorista por debajo del costo",
    queHacer: () =>
      "Corregí el precio mayorista en Precios y costos: tiene que ser mayor que el costo.",
    donde: PRECIOS_Y_COSTOS,
  },
};

/** Lo que se dice de un motivo que este cliente no conoce. */
export const MOTIVO_DESCONOCIDO: MotivoSinRecalcular = {
  que: "que no se pudieron recalcular",
  queHacer: () => "",
};

/** El motivo como clave de la tabla: uno que este cliente no conoce es "". */
export function claveDeMotivo(reason: string | null | undefined): string {
  // Propia, no heredada: "toString" no es un motivo.
  return reason &&
    Object.prototype.hasOwnProperty.call(MOTIVOS_SIN_RECALCULAR, reason)
    ? reason
    : "";
}

/** La posición del motivo en la tabla; el desconocido, al final. */
export function ordenDeMotivo(motivo: string): number {
  const orden = Object.keys(MOTIVOS_SIN_RECALCULAR);
  return orden.includes(motivo) ? orden.indexOf(motivo) : orden.length;
}

export function motivoSinRecalcular(
  reason: string | null | undefined
): MotivoSinRecalcular {
  return MOTIVOS_SIN_RECALCULAR[claveDeMotivo(reason)] ?? MOTIVO_DESCONOCIDO;
}

/** Por qué no se copiaron las fotos: `ImageSkip` en `services/b2b-image-sync.ts`. */
const MOTIVO_SIN_FOTO: Record<string, string> = {
  "sin-imagenes-locales":
    "No tiene fotos cargadas en tu inventario: el catálogo lo muestra sin foto.",
  "sin-producto-local":
    "Ya no está vinculado a un producto de tu inventario: no hay de dónde copiar las fotos.",
};

type Salteada = { reason?: string | null };

/** "2 sin costo o sin precio mayorista en tu inventario y 1 sin una variante…" */
export function describirSalteadas(salteadas: Salteada[]): string {
  const porMotivo = new Map<string, number>();
  for (const { reason } of salteadas) {
    const motivo = motivoSinRecalcular(reason).que;
    porMotivo.set(motivo, (porMotivo.get(motivo) ?? 0) + 1);
  }
  const partes = [...porMotivo].map(([motivo, n]) => `${n} ${motivo}`);
  if (partes.length <= 1) return partes.join("");
  return `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
}

type RespuestaDeActivar = {
  updated?: number;
  skipped?: Salteada[];
  images?: { skipped?: Salteada[] };
};

/**
 * "Activar" un producto desactivado (`POST /products/:id/republish`). Desde
 * v1.72.0 activar vuelve a tomar el precio de tu inventario; la variante que no
 * se puede recalcular queda con el precio que tenía publicado, que puede ser 0.
 */
export function avisoDeActivar(
  respuesta: RespuestaDeActivar | null | undefined
): Aviso {
  const salteadas = respuesta?.skipped ?? [];
  const fotos = MOTIVO_SIN_FOTO[respuesta?.images?.skipped?.[0]?.reason ?? ""];

  if (salteadas.length > 0) {
    const n = salteadas.length;
    const quedaron =
      n === 1
        ? "1 variante quedó con el precio que tenía publicado"
        : `${n} variantes quedaron con el precio que tenían publicado`;
    return {
      title: "Producto activado, con variantes sin actualizar",
      description: `${quedaron}: ${describirSalteadas(salteadas)}.${
        fotos ? ` ${fotos}` : ""
      }`,
      variant: "warning",
      duration: 10000,
    };
  }

  if (fotos) {
    return {
      title: "Producto activado, sin fotos",
      description: `Está otra vez visible en el catálogo B2B. ${fotos}`,
      variant: "warning",
      duration: 8000,
    };
  }

  return {
    title: "Producto activado",
    // `updated` sólo viene desde v1.72.0, que es la que toma el precio de hoy.
    description:
      typeof respuesta?.updated === "number"
        ? "Está otra vez visible en el catálogo B2B, con el precio de hoy de tu inventario."
        : "El producto está nuevamente visible en el catálogo B2B",
    variant: "success",
    duration: 3000,
  };
}

type RespuestaDePublicar = {
  publishId?: string | null;
  totalProducts?: number;
  skipped?: { productId?: string; reason?: string; reasonCode?: string }[];
};

const NO_SE_REACTIVARON = (n: number) =>
  n === 1 ? "1 producto no se reactivó" : `${n} productos no se reactivaron`;

/**
 * El aviso inmediato de "Publicar" (`POST /products/publish-batch`).
 *
 * - **Sin `publishId`** no arrancó ningún workflow, así que ningún evento va a
 *   cerrar nada: el aviso es éste o ninguno. `totalProducts` cuenta los
 *   reactivados.
 * - **Con `publishId`** el acuse y el cierre los pone `useWorkflowMutation`.
 *   Acá sólo se avisa de los desactivados que core no reactivó por no tener
 *   precio (`skipped`, v1.72.0): esos no viajan en el evento de cierre.
 */
export function avisoDePublicarEnLote(
  respuesta: RespuestaDePublicar | null | undefined
): Aviso | null {
  const salteados = respuesta?.skipped ?? [];
  const inactivos = salteados.filter(
    (item) => item.reasonCode === "local_product_unavailable"
  ).length;
  const sinPrecio = salteados.length - inactivos;
  const reactivados = respuesta?.totalProducts ?? 0;

  if (inactivos > 0) {
    const cantidad = salteados.length;
    const motivoInactivo =
      inactivos === 1
        ? "El producto local está inactivo, eliminado o no está disponible para publicar."
        : `${inactivos} productos locales están inactivos, eliminados o no están disponibles para publicar.`;
    return {
      title:
        cantidad === 1
          ? "1 producto no se publicó"
          : `${cantidad} productos no se publicaron`,
      description: `${motivoInactivo}${
        sinPrecio > 0 ? ` ${sinPrecio} más no tenían precio mayorista.` : ""
      }`,
      variant: "warning",
      duration: 10000,
    };
  }
  const motivo =
    "Estaban desactivados y ninguna de sus variantes tiene costo y precio mayorista cargados.";

  if (respuesta?.publishId) {
    if (sinPrecio === 0) return null;
    return {
      title: NO_SE_REACTIVARON(sinPrecio),
      description: motivo,
      variant: "warning",
      duration: 10000,
    };
  }

  if (sinPrecio > 0) {
    return {
      title:
        reactivados > 0
          ? `${reactivados} reactivados, ${sinPrecio} sin precio`
          : NO_SE_REACTIVARON(sinPrecio),
      description:
        reactivados > 0 ? `${NO_SE_REACTIVARON(sinPrecio)}. ${motivo}` : motivo,
      variant: "warning",
      duration: 10000,
    };
  }

  if (reactivados > 0) {
    return {
      title:
        reactivados === 1
          ? "Producto reactivado"
          : `${reactivados} productos reactivados`,
      description: "Están otra vez visibles en el catálogo B2B.",
      variant: "success",
      duration: 4000,
    };
  }

  return {
    title: "No había nada nuevo para publicar",
    description: "Todos los productos elegidos ya estaban publicados.",
    variant: "info",
    duration: 5000,
  };
}

/**
 * El cierre del workflow de publicación (`big_supplier.product_publish.completed`).
 *
 * El intérprete por defecto no sirve acá: no conoce `synced` y cuenta un
 * salteado como un cambio que falló ("Se aplicaron 8 de 10 cambios. 0
 * fallaron."). El payload es el mismo en v1.69.2 y en v1.72.0.
 *
 * Un salteado puede ser un producto sin precio (desde v1.72.0) o uno que otro
 * lote ya había publicado: el evento sólo trae el número, así que se nombran
 * los dos motivos.
 */
export function interpretarFinDePublicacion(
  payload: Record<string, any>
): EventOutcome {
  const publicados = Number(payload?.successCount ?? 0);
  const salteados = Number(payload?.skippedCount ?? 0);
  const fallaron = Number(payload?.failedCount ?? 0);

  if (payload?.status === "failed" && publicados === 0) {
    return {
      isTerminal: true,
      toast: {
        variant: "error",
        title: "No se pudo publicar",
        description:
          "Ningún producto llegó al catálogo B2B. Probá de nuevo en unos minutos.",
        duration: 8000,
      },
    };
  }

  const problemas: string[] = [];
  if (salteados > 0) {
    problemas.push(
      salteados === 1
        ? "1 no se publicó porque no tiene costo y precio mayorista cargados o ya estaba publicado"
        : `${salteados} no se publicaron porque no tienen costo y precio mayorista cargados o ya estaban publicados`
    );
  }
  if (fallaron > 0) {
    problemas.push(fallaron === 1 ? "1 falló" : `${fallaron} fallaron`);
  }

  if (problemas.length === 0) {
    return {
      isTerminal: true,
      toast: {
        variant: "success",
        title: "Productos publicados",
        description:
          publicados === 1
            ? "1 producto ya está en el catálogo B2B."
            : `${publicados} productos ya están en el catálogo B2B.`,
        duration: 4000,
      },
    };
  }

  return {
    isTerminal: true,
    toast: {
      variant: "warning",
      title: "Publicación incompleta",
      description: `Se publicaron ${publicados}. ${problemas.join(" y ")}.`,
      duration: 10000,
    },
  };
}
